/**
 * LotConfieBar — RP44-RP46 : délégation d'un lot de la DPGF / du CCTP.
 *
 * - architecte / chef de projet : « Confier à… » (collaborateur ou partenaire),
 *   « Valider le lot » quand il est « À relire » ;
 * - personne à qui le lot est confié : « Signaler à l'architecte » ;
 * - autres : « Lot confié à X » (lecture seule).
 *
 * Endpoints : PUT /lots-confies/{lot_id}, POST /lots-confies/{lot_id}/signaler,
 *             POST /lots-confies/{lot_id}/valider.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserCircle, PaperPlaneTilt, CheckCircle } from "@phosphor-icons/react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_STYLE = {
  confie: "bg-[#EEF2F7] text-[#1E3A5F]",
  a_relire: "bg-amber-100 text-amber-800",
  valide: "bg-emerald-100 text-emerald-800",
};

export default function LotConfieBar({ chantierId, lotId, lotNom, lotsConfies, variant = "light" }) {
  const [busy, setBusy] = useState(false);
  const { data, refresh, delegationDe } = lotsConfies || {};
  if (!data) return null;
  const moi = data.moi || {};
  const d = delegationDe ? delegationDe(lotId) : null;
  const estMoi = d && d.user_id === moi.user_id;
  if (!d && !moi.peut_confier) return null;

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action impossible");
    } finally {
      setBusy(false);
    }
  };

  const confier = (userId) =>
    run(
      () => axios.put(`${API_URL}/api/chantiers/${chantierId}/lots-confies/${lotId}`, { user_id: userId || null }, { withCredentials: true }),
      userId ? "Lot confié" : "Lot repris"
    );

  const signaler = () => {
    const message = window.prompt("Message pour l'architecte (facultatif)", "");
    if (message === null) return;
    run(
      () => axios.post(`${API_URL}/api/chantiers/${chantierId}/lots-confies/${lotId}/signaler`, { message }, { withCredentials: true }),
      "L'architecte est prévenu : le lot est à relire"
    );
  };

  const valider = () =>
    run(
      () => axios.post(`${API_URL}/api/chantiers/${chantierId}/lots-confies/${lotId}/valider`, {}, { withCredentials: true }),
      "Lot validé"
    );

  const texte = variant === "dark" ? "text-white" : "text-[#2C2C2C]";
  return (
    <div
      className={`flex flex-wrap items-center gap-2 px-3 py-1.5 text-xs border-b border-[#E0E0DB] ${variant === "dark" ? "bg-[#A14828]" : "bg-[#FAFAF8]"}`}
      data-testid={`lot-confie-bar-${lotId}`}
    >
      <UserCircle size={14} className={texte} />
      {d ? (
        <span className={texte} data-testid={`lot-confie-a-${lotId}`}>
          {estMoi ? "Lot qui vous est confié" : `Confié à ${d.user_nom}`}
          {d.partenaire ? " (CCTP)" : ""}
        </span>
      ) : (
        <span className={`${texte} opacity-80`}>Lot non confié</span>
      )}
      {d && (
        <span className={`px-1.5 py-0.5 rounded-sm ${STATUT_STYLE[d.statut] || STATUT_STYLE.confie}`} data-testid={`lot-statut-${lotId}`}>
          {d.statut_libelle}
        </span>
      )}
      {moi.peut_confier && (
        <select
          value={d?.user_id || ""}
          onChange={(e) => confier(e.target.value)}
          disabled={busy}
          className="ml-auto border border-[#E0E0DB] bg-white text-[#2C2C2C] text-xs px-1 py-0.5"
          title={`Confier le lot « ${lotNom || ""} » à une personne : elle seule pourra l'écrire, l'architecte relit et valide.`}
          data-testid={`lot-confier-select-${lotId}`}
        >
          <option value="">{d ? "Reprendre le lot" : "Confier à…"}</option>
          {(data.personnes || []).map((p) => (
            <option key={p.user_id} value={p.user_id}>
              {p.name}{p.partenaire ? " — partenaire" : ""}
            </option>
          ))}
        </select>
      )}
      {estMoi && d.statut !== "valide" && (
        <button
          type="button"
          onClick={signaler}
          disabled={busy}
          className="ml-auto inline-flex items-center gap-1 border border-[#C75B39] bg-white text-[#C75B39] px-2 py-0.5 hover:bg-[#FFF5F2]"
          data-testid={`lot-signaler-${lotId}`}
        >
          <PaperPlaneTilt size={12} /> Signaler à l'architecte
        </button>
      )}
      {moi.peut_valider && d && d.statut === "a_relire" && (
        <button
          type="button"
          onClick={valider}
          disabled={busy}
          className="inline-flex items-center gap-1 border border-[#2D6A4F] bg-white text-[#2D6A4F] px-2 py-0.5 hover:bg-[#E8F3EE]"
          data-testid={`lot-valider-${lotId}`}
        >
          <CheckCircle size={12} /> Valider le lot
        </button>
      )}
    </div>
  );
}
